<template>
  <el-drawer
    :visible.sync="visible"
    direction="rtl"
    :size="drawerWidth"
    :append-to-body="true"
    :before-close="close">

    <template slot="title">
      <div class="history-drawer__title">
        <span class="fs-16" style="font-weight:600;">Historique d'achats</span>
        <span v-if="customerName" class="fs-13 secondary-text" style="margin-left:10px;">— {{ customerName }}</span>
      </div>
      <div class="history-drawer__filters">
        <el-radio-group v-model="years" size="mini" class="history-drawer__years" @change="fetchData">
          <el-radio-button :label="1">1 an</el-radio-button>
          <el-radio-button :label="2">2 ans</el-radio-button>
          <el-radio-button :label="3">3 ans</el-radio-button>
          <el-radio-button :label="4">4 ans</el-radio-button>
          <el-radio-button :label="5">5 ans</el-radio-button>
        </el-radio-group>
      </div>
    </template>

    <div v-loading="loading" class="history-drawer__content scrollable only-y" :style="contentHeight">
      <!-- Statistiques générales -->
      <el-row :gutter="[4, 4]" style="margin-bottom:20px;">
        <el-col :xs="24" :sm="8" class="stat-col">
          <div class="stat-card">
            <div class="stat-card__icon" style="background-color:#f0f9ff;">
              <i class="el-icon-box" style="color:#409eff;font-size:24px;"></i>
            </div>
            <div class="stat-card__content">
              <div class="stat-card__label">Produits uniques</div>
              <div class="stat-card__value">{{ uniqueProductCount }}</div>
            </div>
          </div>
        </el-col>
        <el-col :xs="24" :sm="8" class="stat-col">
          <div class="stat-card">
            <div class="stat-card__icon" style="background-color:#f0fdf4;">
              <i class="el-icon-goods" style="color:#67c23a;font-size:24px;"></i>
            </div>
            <div class="stat-card__content">
              <div class="stat-card__label">Bouteilles commandées</div>
              <div class="stat-card__value">{{ totalBottles.toLocaleString('fr-CA') }}</div>
            </div>
          </div>
        </el-col>
        <el-col :xs="24" :sm="8" class="stat-col">
          <div class="stat-card">
            <div class="stat-card__icon" style="background-color:#fefce8;">
              <i class="el-icon-data-analysis" style="color:#e6a23c;font-size:24px;"></i>
            </div>
            <div class="stat-card__content">
              <div class="stat-card__label">Moy. par mois</div>
              <div class="stat-card__value">{{ avgBottlesPerMonth.toFixed(1) }}</div>
            </div>
          </div>
        </el-col>
        <el-col :xs="24" :sm="8" class="stat-col">
          <div class="stat-card">
            <div class="stat-card__icon" style="background-color:#fce7f3;">
              <i class="el-icon-coin" style="color:#ec407a;font-size:24px;"></i>
            </div>
            <div class="stat-card__content">
              <div class="stat-card__label">Valeur moyenne/bouteille</div>
              <div class="stat-card__value">{{ formatCurrency(avgProductValue) }}</div>
            </div>
          </div>
        </el-col>
        <el-col :xs="24" :sm="8" class="stat-col">
          <div class="stat-card">
            <div class="stat-card__icon" style="background-color:#f3e5f5;">
              <i class="el-icon-discount" style="color:#ab47bc;font-size:24px;"></i>
            </div>
            <div class="stat-card__content">
              <div class="stat-card__label">Frais d'agence/bouteille</div>
              <div class="stat-card__value">{{ formatCurrency(avgAgencyFee) }}</div>
            </div>
          </div>
        </el-col>
      </el-row>

      <el-card shadow="never" class="history-card history-card--top">
        <div slot="header" class="card-header">
          <span class="fs-14" style="font-weight:600;">Caisses achetées par mois (12 derniers mois)</span>
        </div>
        <div v-if="monthlyCases.length === 0 && !loading" class="empty-block">
          Aucun achat trouvé sur les 12 derniers mois.
        </div>
        <div v-else ref="monthlyCasesChart" class="monthly-cases-chart"></div>
      </el-card>

      <el-row :gutter="20">
        <!-- Colonne 1 : Derniers produits achetés -->
        <el-col :xs="24" :sm="24" :md="12">
          <el-card shadow="never" class="history-card">
            <div slot="header" class="card-header">
              <span class="fs-14" style="font-weight:600;">Derniers produits achetés</span>
              <el-button
                size="mini"
                type="text"
                style="float:right;"
                @click="showAllProducts = !showAllProducts">
                {{ showAllProducts ? 'Voir les 5 derniers' : 'Voir tout (' + allRecentProducts.length + ')' }}
              </el-button>
            </div>

            <div v-if="displayedRecentProducts.length === 0 && !loading" class="empty-block">
              Aucun achat trouvé pour cette période.
            </div>

            <div
              v-for="(item, idx) in displayedRecentProducts"
              :key="item.productId + '-' + idx"
              class="product-row">
              <div class="product-row__name">
                <span class="fs-13" style="font-weight:500;">{{ item.productName }}</span>
                <span v-if="item.providerName" class="fs-11 secondary-text" style="margin-left:6px;">{{ item.providerName }}</span>
              </div>
              <div class="product-row__meta fs-11 secondary-text">
                <span v-if="item.uvc">UVC&nbsp;{{ item.uvc }}</span>
                <span v-if="item.uvc && item.lblFormat"> &middot; </span>
                <span v-if="item.lblFormat">{{ item.lblFormat }}</span>
                <span v-else-if="item.volume">{{ item.volume }}&nbsp;ml</span>
                <span v-if="item.sku" style="margin-left:6px;color:#c0c4cc;">({{ item.sku }})</span>
              </div>
              <div class="product-row__stats">
                <el-tag size="mini" type="info" style="margin-right:6px;">Qté&nbsp;{{ item.totalQty }}</el-tag>
                <span v-if="item.unitPrice" class="fs-12 secondary-text">{{ formatCurrency(item.unitPrice) }}&nbsp;/ unit.</span>
                <span class="fs-11 secondary-text" style="margin-left:6px;">{{ formatDate(item.lastDate) }}</span>
              </div>
              <el-divider v-if="idx < displayedRecentProducts.length - 1" style="margin:8px 0;" />
            </div>
          </el-card>
        </el-col>

        <!-- Colonne 2 : Produits les plus achetés -->
        <el-col :xs="24" :sm="24" :md="12">
          <el-card shadow="never" class="history-card">
            <div slot="header" class="card-header">
              <span class="fs-14" style="font-weight:600;">Produits les plus achetés</span>
            </div>

            <div v-if="topProducts.length === 0 && !loading" class="empty-block">
              Aucun produit trouvé pour cette période.
            </div>

            <div
              v-for="(item, idx) in topProducts"
              :key="item.product_id"
              class="top-product-row">
              <div class="top-product-row__header">
                <span class="fs-13" style="font-weight:500;">{{ item.product_name }}</span>
                <span class="fs-12" style="float:right;font-weight:600;">{{ item.total_qty }}&nbsp;u.</span>
              </div>
              <div class="fs-11 secondary-text" style="margin-bottom:4px;">
                <span v-if="item.provider_name">{{ item.provider_name }}</span>
                <span v-if="item.uvc"> &middot; UVC&nbsp;{{ item.uvc }}</span>
                <span v-if="item.format"> &middot; {{ item.format }}</span>
                <span v-else-if="item.volume"> &middot; {{ item.volume }}&nbsp;ml</span>
              </div>
              <el-progress
                :percentage="getQtyPercent(item.total_qty)"
                :show-text="false"
                :stroke-width="6"
                status="success"
                style="margin-bottom:8px;" />
              <el-divider v-if="idx < topProducts.length - 1" style="margin:6px 0;" />
            </div>

            <!-- Stats globales -->
            <div v-if="topProducts.length > 0 || nbOrders > 0" class="summary-stats">
              <el-divider />
              <el-row :gutter="16">
                <el-col :span="12" class="stat-block">
                  <div class="stat-label">Commandes</div>
                  <div class="stat-value">{{ nbOrders }}</div>
                </el-col>
                <el-col :span="12" class="stat-block">
                  <div class="stat-label">Montant total</div>
                  <div class="stat-value">{{ formatCurrency(totalAmount) }}</div>
                </el-col>
              </el-row>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>
  </el-drawer>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import * as echarts from 'echarts';
import SalesOrdersAPI from '@/services/API/sales-order';
import salesAPI from '@/services/API/sales';

export default {
  name: 'CustomerPurchaseHistoryDialog',
  props: {
    customerId: {
      type: Number,
      required: true,
    },
    customerName: {
      type: String,
      default: null,
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      window: {
        width: 0,
        height: 0,
      },
      loading: false,
      years: 2,
      showAllProducts: false,
      allRecentProducts: [],
      topProducts: [],
      monthlyCases: [],
      monthlyCasesByProduct: [],
      productTotalsForChart: [],
      monthlyCasesChartInstance: null,
      nbOrders: 0,
      totalAmount: 0,
      uniqueProductCount: 0,
      totalBottles: 0,
      avgBottlesPerMonth: 0,
      avgProductValue: 0,
      avgAgencyFee: 0,
    };
  },
  computed: {
    drawerWidth() {
      return this.window.width > 1024 ? '75%' : '100%';
    },
    contentHeight() {
      const offset = this.window.width > 1024 ? 90 : 120;
      return `height:${Math.max(this.window.height - offset, 320)}px;padding:16px 20px;`;
    },
    displayedRecentProducts() {
      return this.showAllProducts ? this.allRecentProducts : this.allRecentProducts.slice(0, 5);
    },
    maxQty() {
      return _.maxBy(this.topProducts, 'total_qty');
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.fetchData();
      } else {
        this.disposeMonthlyCasesChart();
      }
    },
    monthlyCasesByProduct() {
      this.$nextTick(() => {
        this.renderMonthlyCasesChart();
      });
    }
  },
  methods: {
    handleResize() {
      this.window.width = window.innerWidth;
      this.window.height = window.innerHeight;
    },
    close() {
      this.$emit('close');
    },
    fetchData() {
      if (!this.customerId) return;

      const startDate = moment().subtract(this.years, 'years').startOf('day').format('YYYY-MM-DD Z');
      const endDate = moment().add(1, 'day').format('YYYY-MM-DD Z');

      this.loading = true;

      SalesOrdersAPI.fetchSalesOrders({
        customerId: this.customerId,
        startDate,
        endDate,
        paging: 'false',
        orderBy: 'date',
        direction: 'desc',
      }, 'productInventory')
        .then((salesResult) => {
          const orders = _.isArray(salesResult) ? salesResult : (_.get(salesResult, 'list', []) || []);

          // --- Stats globales ---
          this.nbOrders = orders.length;
          this.totalAmount = _.sumBy(orders, o => Number(_.get(o, 'total', 0)));

          // --- Construire la map produits depuis les lignes des commandes ---
          const productMap = {};
          const monthBuckets = {};
          const monthBucketsByProduct = {};
          const productTotalsForChart = {};
          const monthStart = moment().startOf('month').subtract(11, 'months');
          const monthEnd = moment().endOf('month');

          // Pre-remplir les 12 mois pour toujours afficher une série complète.
          for (let i = 0; i < 12; i += 1) {
            const m = monthStart.clone().add(i, 'months');
            monthBuckets[m.format('YYYY-MM')] = {
              monthKey: m.format('YYYY-MM'),
              monthLabel: m.format('MMM YY'),
              cases: 0,
            };

            monthBucketsByProduct[m.format('YYYY-MM')] = {
              monthKey: m.format('YYYY-MM'),
              monthLabel: m.format('MMM YY'),
              products: {},
            };
          }

          _.forEach(orders, (order) => {
            const orderDate = _.get(order, 'date');
            const orderMoment = moment(orderDate);
            _.forEach(_.get(order, 'lines', []), (line) => {
              const pId = _.get(line, 'product.id') || _.get(line, 'puid');
              if (!pId) return;
              if (!productMap[pId]) {
                productMap[pId] = {
                  productId: pId,
                  product_id: pId,
                  productName: _.get(line, 'product.name', ''),
                  product_name: _.get(line, 'product.name', ''),
                  providerName: _.get(line, 'product.provider.name', ''),
                  provider_name: _.get(line, 'product.provider.name', ''),
                  sku: _.get(line, 'product.sku', ''),
                  uvc: _.get(line, 'product.uvc', null),
                  lblFormat: _.get(line, 'product.format', null) || _.get(line, 'product.lblFormat', null),
                  format: _.get(line, 'product.format', null) || _.get(line, 'product.lblFormat', null),
                  volume: _.get(line, 'product.volume', null),
                  unitPrice: Number(_.get(line, 'product.sellingUnitPrice', 0) || _.get(line, 'product.portausCompany.sellingUnitPrice', 0) || 0),
                  agencyFee: Number(_.get(line, 'product.pricing.agencyFee', 0) || _.get(line, 'agencyFee', 0) || 0),
                  totalQty: 0,
                  total_qty: 0,
                  totalValue: 0,
                  totalAgencyFee: 0,
                  lastDate: orderDate,
                };
              }
              const qty = Number(_.get(line, 'qty', 0) || _.get(line, 'qty_ordered', 0));
              const unitPrice = Number(_.get(line, 'product.sellingUnitPrice', 0) || _.get(line, 'product.portausCompany.sellingUnitPrice', 0) || 0);
              const agencyFee = Number(_.get(line, 'product.pricing.agencyFee', 0) || _.get(line, 'agencyFee', 0) || 0);
              productMap[pId].totalQty += qty;
              productMap[pId].total_qty += qty;
              productMap[pId].totalValue += qty * unitPrice;
              productMap[pId].totalAgencyFee += qty * agencyFee;

              // Hypothese: qty represente les bouteilles; conversion en caisses via UVC.
              if (orderMoment.isValid() && orderMoment.isBetween(monthStart, monthEnd, null, '[]')) {
                const monthKey = orderMoment.format('YYYY-MM');
                const uvc = Number(_.get(line, 'product.uvc', 0) || _.get(line, 'uvc', 0));
                const cases = uvc > 0 ? qty / uvc : qty;
                const productName = _.get(line, 'product.name', `Produit ${pId}`);
                if (monthBuckets[monthKey]) {
                  monthBuckets[monthKey].cases += cases;
                }

                if (monthBucketsByProduct[monthKey]) {
                  const monthProduct = monthBucketsByProduct[monthKey].products;
                  if (!monthProduct[pId]) {
                    monthProduct[pId] = {
                      productId: pId,
                      productName,
                      cases: 0,
                    };
                  }
                  monthProduct[pId].cases += cases;
                }

                if (!productTotalsForChart[pId]) {
                  productTotalsForChart[pId] = {
                    productId: pId,
                    productName,
                    cases: 0,
                  };
                }
                productTotalsForChart[pId].cases += cases;
              }

              if (!productMap[pId].lastDate || orderDate > productMap[pId].lastDate) {
                productMap[pId].lastDate = orderDate;
              }
            });
          });

          const productList = Object.values(productMap);

          // --- Statistiques supplémentaires ---
          this.uniqueProductCount = productList.length;
          this.totalBottles = _.sumBy(productList, 'totalQty');
          const monthsWithSales = _.sumBy(Object.values(monthBuckets), m => m.cases > 0 ? 1 : 0);
          this.avgBottlesPerMonth = monthsWithSales > 0 ? this.totalBottles / 12 : 0;
          const totalProductValue = _.sumBy(productList, 'totalValue');
          const totalAgencyFee = _.sumBy(productList, 'totalAgencyFee');
          this.avgProductValue = this.totalBottles > 0 ? totalProductValue / this.totalBottles : 0;
          this.avgAgencyFee = this.totalBottles > 0 ? totalAgencyFee / this.totalBottles : 0;

          // --- Colonne 1 : triés par date de dernière commande ---
          this.allRecentProducts = _.orderBy(productList, ['lastDate'], ['desc']);

          // --- Colonne 2 : triés par quantité totale ---
          this.topProducts = _.orderBy(productList, ['total_qty'], ['desc']);

          // --- Serie 12 mois (incluant le mois courant), du plus récent au plus ancien ---
          this.monthlyCases = _.orderBy(Object.values(monthBuckets), ['monthKey'], ['desc']);
          this.monthlyCasesByProduct = _.orderBy(Object.values(monthBucketsByProduct), ['monthKey'], ['desc']);

          this.productTotalsForChart = _.orderBy(Object.values(productTotalsForChart), ['cases'], ['desc']);
        })
        .catch(() => {
          this.$message.error("Une erreur est survenue lors du chargement de l'historique.");
        })
        .finally(() => {
          this.loading = false;
          this.$nextTick(() => {
            this.renderMonthlyCasesChart();
          });
        });
    },
    getQtyPercent(qty) {
      const max = _.get(this.maxQty, 'total_qty', 1) || 1;
      return Math.round((qty / max) * 100);
    },
    formatDate(date) {
      return date ? moment(date).format('DD/MM/YY') : '';
    },
    formatCases(value) {
      const n = Number(value || 0);
      return `${n.toFixed(2)} caisse${n > 1 ? 's' : ''}`;
    },
    renderMonthlyCasesChart() {
      if (!this.visible || !this.$refs.monthlyCasesChart || !this.monthlyCasesByProduct.length) {
        return;
      }

      const ordered = _.orderBy(this.monthlyCasesByProduct, ['monthKey'], ['asc']);
      const categories = ordered.map(i => i.monthLabel);

      const sortedProducts = _.orderBy(this.productTotalsForChart || [], ['cases'], ['desc']);

      const baseSeries = sortedProducts.map((product) => ({
        name: product.productName,
        type: 'bar',
        stack: 'cases',
        barMaxWidth: 40,
        data: ordered.map((month) => {
          const p = _.get(month, `products.${product.productId}`);
          return Number(_.get(p, 'cases', 0).toFixed(2));
        }),
        emphasis: { focus: 'series' },
      }));

      const monthStackExtremities = ordered.map((_, monthIndex) => {
        let bottomSeriesIndex = -1;
        let topSeriesIndex = -1;

        baseSeries.forEach((s, sIndex) => {
          const v = Number(s.data[monthIndex] || 0);
          if (v > 0) {
            if (bottomSeriesIndex === -1) bottomSeriesIndex = sIndex;
            topSeriesIndex = sIndex;
          }
        });

        return { bottomSeriesIndex, topSeriesIndex };
      });

      const radius = 6;
      const series = baseSeries.map((s, sIndex) => ({
        ...s,
        data: s.data.map((v, monthIndex) => {
          const value = Number(v || 0);
          if (value <= 0) return value;

          const { bottomSeriesIndex, topSeriesIndex } = monthStackExtremities[monthIndex];
          if (bottomSeriesIndex === -1 || topSeriesIndex === -1) return value;

          if (bottomSeriesIndex === sIndex && topSeriesIndex === sIndex) {
            return {
              value,
              itemStyle: { borderRadius: [radius, radius, radius, radius] },
            };
          }

          if (topSeriesIndex === sIndex) {
            return {
              value,
              itemStyle: { borderRadius: [radius, radius, 0, 0] },
            };
          }

          if (bottomSeriesIndex === sIndex) {
            return {
              value,
              itemStyle: { borderRadius: [0, 0, radius, radius] },
            };
          }

          return value;
        }),
      }));

      if (!this.monthlyCasesChartInstance) {
        this.monthlyCasesChartInstance = echarts.init(this.$refs.monthlyCasesChart);
      }

      this.monthlyCasesChartInstance.setOption({
        color: ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'],
        grid: { left: 36, right: 16, top: 56, bottom: 30 },
        legend: {
          show: false,
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            if (!params || !params.length) return '';
            const rows = params
              .filter(p => Number(p.value) > 0)
              .sort((a, b) => Number(b.value) - Number(a.value))
              .map(p => `${p.marker} ${p.seriesName}: <b>${Number(p.value).toFixed(2)}</b> caisses`)
              .join('<br/>');

            const total = _.sumBy(params, p => Number(p.value || 0));
            return `<div><b>${params[0].axisValue}</b></div>${rows}<br/><div>Total: <b>${total.toFixed(2)}</b> caisses</div>`;
          },
        },
        xAxis: {
          type: 'category',
          data: categories,
          axisTick: { alignWithLabel: true },
        },
        yAxis: {
          type: 'value',
          name: 'Caisses',
        },
        series,
      });

      this.monthlyCasesChartInstance.resize();
    },
    disposeMonthlyCasesChart() {
      if (this.monthlyCasesChartInstance) {
        this.monthlyCasesChartInstance.dispose();
        this.monthlyCasesChartInstance = null;
      }
    },
    formatCurrency(value) {
      const n = Number(value || 0);
      return n.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 });
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
    this.fetchData();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.disposeMonthlyCasesChart();
  }
};
</script>

<style scoped>
.history-card {
  height: auto;
}
.history-card--top {
  margin-bottom: 12px;
}
.history-card >>> .el-card__body {
  padding: 12px 16px;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.product-row {
  padding: 4px 0;
}
.product-row__name {
  margin-bottom: 2px;
}
.product-row__meta {
  margin-bottom: 3px;
}
.product-row__stats {
  display: flex;
  align-items: center;
}
.top-product-row__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2px;
}
.summary-stats {
  margin-top: 4px;
}
.stat-block {
  text-align: center;
}
.stat-label {
  font-size: 11px;
  color: #909399;
}
.stat-value {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}
.empty-block {
  color: #909399;
  text-align: center;
  padding: 24px 0;
  font-size: 13px;
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 12px;
  background-color: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  height: 100%;
}

.stat-col {
  display: flex;
  flex-direction: column;
}

.stat-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 4px;
  margin-right: 12px;
  flex-shrink: 0;
}

.stat-card__content {
  flex: 1;
}

.stat-card__label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.stat-card__value {
  font-size: 22px;
  font-weight: 600;
  color: #303133;
}

.monthly-cases-chart {
  width: 100%;
  height: 320px;
}

.history-drawer__content {
  box-sizing: border-box;
}

.history-drawer__filters {
  margin-top: 6px;
}

@media (max-width: 768px) {
  .history-drawer__title {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .history-drawer__years {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    gap: 6px;
  }

  .history-drawer__years >>> .el-radio-button {
    flex: 1 1 calc(33.33% - 6px);
    min-width: 0;
  }

  .history-drawer__years >>> .el-radio-button__inner {
    width: 100%;
    padding: 8px 0;
    font-size: 12px;
    border-left: 1px solid #dcdfe6;
    border-radius: 4px;
    text-align: center;
  }

  .history-drawer__years >>> .el-radio-button:first-child .el-radio-button__inner,
  .history-drawer__years >>> .el-radio-button__inner:hover {
    border-left: 1px solid #dcdfe6;
  }

  .history-drawer__years >>> .el-radio-button.is-active .el-radio-button__inner {
    border-left: 1px solid #409eff;
    border-color: #409eff;
  }
}
</style>
